import api from '../config/axios';

/**
 * @class CalendarService
 * @description Servizio per la comunicazione con le API del calendario
 */
class CalendarService {
  
  // =================== HELPER METHODS ===================
  
  /**
   * Helper per date sicure
   */
  createSafeDate(dateValue) {
    try {
      if (!dateValue) return null;
      
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }
      
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
      return null;
    }
  }

  /**
   * Helper per toISOString sicuro
   */
  safeToISOString(date) {
    try {
      if (!date) return null;
      
      if (typeof date === 'string') {
        date = new Date(date);
      }
      
      return date.toISOString();
    } catch (error) {
      return null;
    }
  }
  
  // =================== EVENTI BASE ===================
  
  /**
   * 📅 Recupera eventi per periodo (principale per vista settimanale)
   */
  async getEvents(startDate, endDate, includeParticipants = true) {
    try {
      const response = await api.get('/calendar/events', {
        params: {
          startDate,
          endDate,
          includeParticipants: includeParticipants ? 'true' : 'false'
        }
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore getEvents:', error);
      throw error;
    }
  }

  /**
   * 📊 Recupera eventi della settimana corrente (DEFAULT)
   */
  async getWeekEvents(startDate, endDate) {
    return await this.getEvents(startDate, endDate, true);
  }

  /**
   * 📅 Recupera eventi di oggi
   */
  async getTodayEvents() {
    try {
      const response = await api.get('/calendar/today');
      return response.data?.data?.events || response.data?.events || [];
    } catch (error) {
      console.error('Errore getTodayEvents:', error);
      throw error;
    }
  }

  /**
   * 📊 Recupera eventi della settimana
   */
  async getThisWeekEvents() {
    try {
      const response = await api.get('/calendar/week');
      return response.data?.data?.events || response.data?.events || [];
    } catch (error) {
      console.error('Errore getThisWeekEvents:', error);
      throw error;
    }
  }

  /**
   * 🗓️ Recupera eventi di un mese specifico
   */
  async getMonthEvents(year, month) {
    try {
      const response = await api.get(`/calendar/month/${year}/${month}`);
      return response.data?.data?.events || response.data?.events || [];
    } catch (error) {
      console.error('Errore getMonthEvents:', error);
      throw error;
    }
  }

  /**
   * 📆 Recupera eventi di un anno
   */
  async getYearEvents(year) {
    try {
      const response = await api.get(`/calendar/year/${year}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore getYearEvents:', error);
      throw error;
    }
  }

  /**
   * 📈 Recupera statistiche mensili ferie/permessi per utente
   */
  async getMonthlyStats(year, month) {
    try {
      const response = await api.get(`/calendar/monthly-stats/${year}/${month}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore getMonthlyStats:', error);
      throw error;
    }
  }

  // =================== CRUD EVENTI ===================
  
  /**
   * ➕ Crea nuovo evento
   */
  async createEvent(eventData) {
    try {
      const response = await api.post('/calendar/events', eventData);
      return response.data?.data?.event || response.data?.event || response.data;
    } catch (error) {
      console.error('Errore createEvent:', error);
      throw error;
    }
  }

  /**
   * ✏️ Aggiorna evento esistente
   */
  async updateEvent(eventId, eventData) {
    try {
      const response = await api.put(`/calendar/events/${eventId}`, eventData);
      return response.data?.data?.event || response.data?.event || response.data;
    } catch (error) {
      console.error('Errore updateEvent:', error);
      throw error;
    }
  }

  /**
   * 🗑️ Elimina evento
   */
  async deleteEvent(eventId) {
    try {
      const response = await api.delete(`/calendar/events/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Errore deleteEvent:', error);
      throw error;
    }
  }

  // =================== SUPPORTO E LOOKUP ===================

  /**
   * 📋 Recupera tipi evento disponibili
   */
  async getEventTypes() {
    try {
      const response = await api.get('/calendar/event-types');
      
      if (response.data && response.data.records) {
        const eventTypes = response.data.records.map(record => ({
          id: record.TEV_Id_TipiEventi,
          name: record.TEV_Descrizione,
          description: record.TEV_Descrizione,
          code: record.TEV_Codice,
          color: this.getEventTypeColor(record.TEV_Id_TipiEventi),
          isActive: record.TEV_StatoRecord === 'A'
        }));
        
        return eventTypes;
      }
      
      return [];
    } catch (error) {
      console.error('Errore caricamento tipi evento:', error);
      throw error;
    }
  }

  /**
   * ✅ NUOVO: Ottiene info tipo evento per ID
   */
  getEventTypeById(tipoEventoId) {
    const eventTypes = this.eventTypesCache || [];
    return eventTypes.find(tipo => tipo.TEV_Id_TipiEventi === parseInt(tipoEventoId));
  }

  /**
   * ✅ NUOVO: Cache tipi eventi per performance
   */
  async loadAndCacheEventTypes() {
    try {
      if (!this.eventTypesCache) {
        this.eventTypesCache = await this.getEventTypes();
      }
      return this.eventTypesCache;
    } catch (error) {
      console.error('❌ Errore cache tipi eventi:', error);
      return [];
    }
  }

  /**
   * 👥 Recupera utenti disponibili per partecipanti
   */
  async getAvailableUsers() {
    try {
      const response = await api.get('/calendar/users');
      return response.data?.data?.users || response.data?.users || [];
    } catch (error) {
      console.error('Errore getAvailableUsers:', error);
      throw error;
    }
  }

  // =================== CONFLITTI E VALIDAZIONI ===================

  /**
   * ⚠️ Controlla conflitti per date/ore specifiche
   */
  async checkConflicts(eventData) {
    try {
      const response = await api.post('/calendar/events/check-conflicts', {
        CAL_DataOraInizio: eventData.CAL_DataOraInizio,
        CAL_DataOraFine: eventData.CAL_DataOraFine,
        excludeEventId: eventData.excludeEventId || null
      });
      return response.data?.data?.conflicts || response.data?.conflicts || [];
    } catch (error) {
      console.error('Errore checkConflicts:', error);
      throw error;
    }
  }

  // =================== NOTIFICHE E REMINDER ===================

  /**
   * 🔔 Recupera notifiche personalizzate per utente corrente
   */
  async getPersonalReminders(hours = 24) {
    try {
      const response = await api.get('/calendar/reminders', {
        params: { hours }
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore getPersonalReminders:', error);
      throw error;
    }
  }

  /**
   * ✅ Marca notifiche come lette
   */
  async markNotificationsAsRead(notificationIds) {
    try {
      const response = await api.post('/calendar/reminders/mark-read', {
        notificationIds
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore markNotificationsAsRead:', error);
      throw error;
    }
  }

  /**
   * 📖 Ottieni lista notifiche già lette
   */
  async getReadNotifications() {
    try {
      const response = await api.get('/calendar/reminders/read-status');
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore getReadNotifications:', error);
      throw error;
    }
  }

  // =================== STATISTICHE ===================

  /**
   * 📊 Recupera statistiche calendario
   */
  async getStatistics(startDate, endDate) {
    try {
      const response = await api.get('/calendar/statistics', {
        params: { startDate, endDate }
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Errore getStatistics:', error);
      throw error;
    }
  }

  // =================== UTILITY METHODS ===================

  /**
   * 📅 Calcola inizio e fine settimana per una data
   */
  getWeekBounds(date = new Date()) {
    try {
      const safeDate = this.createSafeDate(date);
      const currentDate = new Date(safeDate);
      
      // Calcola inizio settimana (lunedì)
      const dayOfWeek = currentDate.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() + diffToMonday);
      startOfWeek.setHours(0, 0, 0, 0);
      
      // Calcola fine settimana (domenica)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      
      return {
        startDate: startOfWeek,
        endDate: endOfWeek,
        start: this.safeToISOString(startOfWeek).split('T')[0],
        end: this.safeToISOString(endOfWeek).split('T')[0],
      };
    } catch (error) {
      console.warn('Errore getWeekBounds:', error);
      // Fallback to current week
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay() + 1);
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      
      return {
        startDate: startOfWeek,
        endDate: endOfWeek,
        start: startOfWeek.toISOString().split('T')[0],
        end: endOfWeek.toISOString().split('T')[0],
      };
    }
  }

  /**
   * 📅 Calcola i 7 giorni della settimana per una data (AGGIUNTO per useCalendar)
   */
  getWeekDates(date = new Date()) {
    const d = new Date(date);
    const day = d.getDay();
    
    // Calcola lunedì (inizio settimana)
    const startOfWeek = new Date(d);
    startOfWeek.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    
    // Genera array di 7 giorni
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    
    return weekDays;
  }

  /**
   * 📅 Calcola inizio e fine mese per una data
   */
  getMonthBounds(date = new Date()) {
    try {
      const safeDate = this.createSafeDate(date);
      const currentDate = new Date(safeDate);
      
      // Primo giorno del mese
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      // Ultimo giorno del mese
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      
      return {
        startDate: startOfMonth,
        endDate: endOfMonth,
        start: this.safeToISOString(startOfMonth).split('T')[0],
        end: this.safeToISOString(endOfMonth).split('T')[0],
      };
    } catch (error) {
      console.warn('Errore getMonthBounds:', error);
      // Fallback to current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      return {
        startDate: startOfMonth,
        endDate: endOfMonth,
        start: startOfMonth.toISOString().split('T')[0],
        end: endOfMonth.toISOString().split('T')[0],
      };
    }
  }

  /**
   * 🔄 Serializza partecipanti per backend
   */
  serializeParticipants(participants) {
    if (!Array.isArray(participants)) return '[]';
    
    const validParticipants = participants
      .map(p => typeof p === 'number' ? p : parseInt(p))
      .filter(p => !isNaN(p));
    
    return JSON.stringify(validParticipants);
  }

  /**
   * 🔄 Deserializza partecipanti dal backend
   */
  parseParticipants(participantsString) {
    try {
      if (!participantsString || participantsString.trim() === '') return [];
      
      const participants = JSON.parse(participantsString);
      if (!Array.isArray(participants)) return [];
      
      return participants.map(id => parseInt(id)).filter(id => !isNaN(id));
    } catch (error) {
      console.warn('Errore parsing partecipanti:', error);
      return [];
    }
  }

  /**
   * 🎨 Ottiene colore per tipo evento
   */
  getEventTypeColor(eventTypeId) {
    const colors = {
      1: '#6B7280', // Sede - Gray
      2: '#F59E0B', // Ferie - Amber  
      3: '#8B5CF6', // Permessi - Purple
      4: '#3B82F6', // WebMeeting - Blue
      5: '#10B981', // Riunione - Green
      7: '#EC4899', // Attività (se esiste ID 7) - Pink
      8: '#F97316', // Attività - Orange (distinto da Sede)
      9: '#EF4444'  // Malattia - Red
    };
    
    return colors[eventTypeId] || '#6B7280';
  }

  /**
   * 🎨 Ottiene icona per tipo evento
   */
  getEventTypeIcon(eventTypeId) {
    const icons = {
      1: '🏢', // Sede
      2: '🏖️', // Ferie
      3: '⏰', // Permessi  
      4: '💻', // WebMeeting
      5: '👥', // Riunione
      9: '🏥'  // Malattia
    };
    
    return icons[eventTypeId] || '📅';
  }

  /**
   * 📅 Ottiene nome del mese per una data (AGGIUNTO per useCalendar)
   */
  getMonthName(date = new Date()) {
    const monthNames = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];
    
    const d = new Date(date);
    return monthNames[d.getMonth()];
  }

  /**
   * 📅 Ottiene nome del giorno per una data
   */
  getDayName(date = new Date()) {
    const dayNames = [
      'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'
    ];
    
    const d = new Date(date);
    return dayNames[d.getDay()];
  }

  /**
   * 📅 Formatta data in formato italiano
   */
  formatDate(date, format = 'long') {
    const d = new Date(date);
    
    if (format === 'short') {
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
    
    if (format === 'medium') {
      return d.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    
    // format === 'long'
    return d.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  // =================== TIMBRATURE ===================

  /**
   * Scarica il file timbrature per il periodo specificato
   * @param {number} anno
   * @param {number} mese - 1-12
   */
  async downloadTimbrature(anno, mese) {
    const dataInizio = `${anno}-${String(mese).padStart(2, '0')}-01`;
    const ultimoGiorno = new Date(anno, mese, 0).getDate();
    const dataFine = `${anno}-${String(mese).padStart(2, '0')}-${String(ultimoGiorno).padStart(2, '0')}`;

    const response = await api.get('/timbrature/genera', {
      params: { dataInizio, dataFine },
      responseType: 'blob'
    });

    // Trigger download nel browser
    const blob = new Blob([response.data], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timbrature_${dataInizio}_${dataFine}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Spedisce le timbrature a Siclamm
   * @param {number} anno
   * @param {number} mese - 1-12
   */
  async spedisciTimbrature(anno, mese) {
    const dataInizio = `${anno}-${String(mese).padStart(2, '0')}-01`;
    const ultimoGiorno = new Date(anno, mese, 0).getDate();
    const dataFine = `${anno}-${String(mese).padStart(2, '0')}-${String(ultimoGiorno).padStart(2, '0')}`;

    const response = await api.post('/timbrature/spedisci', { dataInizio, dataFine });
    return response.data;
  }
}

// ✅ FIX: Assign to variable before exporting
const calendarServiceInstance = new CalendarService();
export default calendarServiceInstance;